import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { targetLang } from "@/lib/babel";

// Babel auto-translate (cc_24): when a chat's auto_translate is on, translate incoming text —
// the last 20 in one /translate/batch on open, then each live message via /translate. Sealed
// (Cipher Lock) text is sent with ephemeral:true so the server never stores it. A 429 pauses
// auto mode (the shared UpsellSheet handles the upsell).
export function useAutoTranslate({ enabled, messages, plain, user, conversationId }) {
  const [translations, setTranslations] = useState({}); // messageId -> {translated, source_lang}
  const [paused, setPaused] = useState(false);
  const inflight = useRef(new Set());
  const batched = useRef(false);
  const target = targetLang(user);

  useEffect(() => {
    setTranslations({});
    setPaused(false);
    inflight.current.clear();
    batched.current = false;
  }, [conversationId]);

  useEffect(() => {
    if (!enabled || paused || !user?.id) return undefined;
    const textOf = (m) => (m.e2ee ? plain?.[m.id]?.text : m.text) || "";
    const todo = messages.filter((m) => m.sender_id !== user.id && !m.deleted && !(m.id in translations) && !inflight.current.has(m.id) && textOf(m).trim().length > 1);
    if (!todo.length) return undefined;
    let cancelled = false;
    const sealed = todo.some((m) => !!m.e2ee);
    const items = (batched.current ? todo : todo.slice(-20)).map((m) => ({ id: m.id, text: textOf(m) }));
    items.forEach((i) => inflight.current.add(i.id));
    (async () => {
      try {
        let results = {};
        if (items.length === 1 && batched.current) {
          const r = await api.translateText(items[0].text, sealed);
          results[items[0].id] = { translated: r.translated, source_lang: r.source_lang };
        } else {
          const r = await api.translateBatch(items, sealed);
          results = r.results || {};
        }
        batched.current = true;
        if (cancelled) return;
        const next = {};
        for (const i of items) {
          const r = results[i.id];
          // null translated = already in the target language → mark as seen so we don't retry
          next[i.id] = r && r.translated && r.source_lang !== target ? { translated: r.translated, source_lang: r.source_lang } : null;
        }
        setTranslations((t) => ({ ...t, ...next }));
      } catch (e) {
        if (e?.response?.status === 429) setPaused(true);
        else if (!cancelled) setTranslations((t) => ({ ...t, ...Object.fromEntries(items.map((i) => [i.id, null])) }));
      } finally {
        items.forEach((i) => inflight.current.delete(i.id));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, paused, messages, plain, user?.id]);

  return { translations, paused, resume: () => setPaused(false) };
}
